import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  createContext,
  useContext,
} from 'react';
import cx from 'clsx';

import { useDeviceTypeIsMobile, useSwitch } from '@/hooks';
import { useCurrentUserId } from '@/utils';

import style from './index.module.scss';
import { Translation, useTranslation } from 'react-i18next';
import { useAutoTranslateUGC, useLocale } from '@/utils/i18n';
import { I18nSwitchModal } from '../DesktopHeader/I18nSwitch';

type Props = {
  children?: ReactNode;
  btnClassName?: string;
  canTranslated?: boolean;
  creatorId?: string;
  contentType: 'post' | 'message' | 'comment';
  hideManualTransBtn?: boolean;
  hideShowOriginBtn?: boolean;
};

const TranslateBtnTextMap = {
  post: <Translation>{t => t('Translated.translatePost')}</Translation>,
  message: <Translation>{t => t('Translated.translateMessage')}</Translation>,
  comment: <Translation>{t => t('Translated.translateComment')}</Translation>,
};

const TranslateContext = createContext({ on: false });

export const Translated = ({
  children,
  btnClassName,
  canTranslated,
  creatorId,
  contentType,
  hideManualTransBtn,
  hideShowOriginBtn,
}: Props) => {
  const { t } = useTranslation();
  const userId = useCurrentUserId();
  const [currentLocale] = useLocale();
  const { autoTranslateUGC } = useAutoTranslateUGC();
  const { on, toggle, turn } = useSwitch(
    autoTranslateUGC && canTranslated && (!creatorId || userId !== creatorId),
  );
  const { on: isShowI18nModal, turnOn: showI18nModal, turnOff: hideI18nModal } = useSwitch();
  const isMobile = useDeviceTypeIsMobile();

  const translateBtnText = TranslateBtnTextMap[contentType];

  const showManualTransBtn = useMemo(
    () =>
      ((!currentLocale && !isMobile) || (currentLocale && !autoTranslateUGC && canTranslated)) &&
      !hideManualTransBtn,
    [currentLocale, isMobile, autoTranslateUGC, canTranslated, hideManualTransBtn],
  );

  const showShowOriginBtn = useMemo(
    () => autoTranslateUGC && canTranslated && !hideShowOriginBtn,
    [autoTranslateUGC, canTranslated, hideShowOriginBtn],
  );

  useEffect(() => {
    turn(!!canTranslated && autoTranslateUGC);
    // eslint-disable-next-line
  }, [canTranslated, autoTranslateUGC]);

  useEffect(() => {
    if (creatorId === userId) {
      turn(false);
    }
    // eslint-disable-next-line
  }, [userId]);

  const handleToggle = useCallback(
    (e: React.MouseEvent<HTMLSpanElement>) => {
      if (!canTranslated) {
        return;
      }

      e.stopPropagation();
      toggle();
    },
    [canTranslated, toggle],
  );

  const handleClickManualTranslate = useCallback(
    e => {
      e.stopPropagation();
      if (!currentLocale) {
        showI18nModal();
        return;
      }
      toggle();
    },
    [currentLocale, showI18nModal, toggle],
  );

  const contextProps = useMemo(() => ({ on }), [on]);

  return (
    <>
      <TranslateContext.Provider value={contextProps}>{children}</TranslateContext.Provider>
      {showShowOriginBtn ? (
        <div className={cx(style.translated, btnClassName)}>
          <span className={style.btn} onClick={handleToggle}>
            {on ? t('Translated.original-text') : t('Translated.translated')}
          </span>
        </div>
      ) : null}
      {showManualTransBtn ? (
        <div
          className={cx(
            style.translated,
            style.left,
            contentType === 'post' && style.post,
            btnClassName,
          )}>
          <span className={style.btn} onClick={handleClickManualTranslate}>
            {on ? t('Translated.showOriginal') : translateBtnText}
          </span>
        </div>
      ) : null}
      {isShowI18nModal ? <I18nSwitchModal turnOff={hideI18nModal} /> : null}
    </>
  );
};

export const useTranslateOpen = () => {
  const { on } = useContext(TranslateContext);

  return on;
};

export const TernaryTranslate = ({
  original,
  translated,
  children,
}: {
  original: ReactNode;
  children?: ReactNode;
  translated?: ReactNode;
}) => {
  const on = useTranslateOpen();

  return (
    <>
      {children}
      {on ? translated : original}
    </>
  );
};
